'use client';

import { useEffect, useState } from 'react';
import { colors } from '@/constant/colors';
import report, { ReportEvent } from '@/lib/report';
import NormalButton from '@/components/Button/normal';
import {
  isChunkLoadError,
  autoReloadOnce,
  hasAutoReloadedThisSession,
  getBuildId,
  getNetworkInfo,
} from '@/lib/chunk-error-handler';

/**
 * Next.js App Router 错误页面
 *
 * 此文件会自动捕获 app 目录下所有路由的错误
 * 注意：必须是 'use client' 组件
 *
 * 行为：
 * - ChunkLoadError: 自动重载一次（静默），失败则显示刷新按钮
 * - 其他错误: 记录错误，显示友好提示，提供返回首页按钮
 *
 * 参考：https://nextjs.org/docs/app/building-your-application/routing/error-handling
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [isChunkError, setIsChunkError] = useState(false);
  const [shouldShowUI, setShouldShowUI] = useState(false);

  useEffect(() => {
    // 记录错误到控制台
    console.error('Page error caught by error.tsx:', error);

    const isChunk = isChunkLoadError(error);
    setIsChunkError(isChunk);

    if (isChunk) {
      // Handle ChunkLoadError
      const alreadyReloaded = hasAutoReloadedThisSession();
      const willReload = !alreadyReloaded;

      // Report to PostHog with chunk-specific info
      report(ReportEvent.ERROR_BOUNDARY_TRIGGERED, {
        error_type: 'chunk_load_error',
        error_message: error.message,
        error_digest: error.digest,
        error_stack: error.stack,
        pathname: window.location.pathname,
        href: window.location.href,
        build_id: getBuildId(),
        user_agent: navigator.userAgent,
        did_auto_reload: willReload,
        already_reloaded: alreadyReloaded,
        ...getNetworkInfo(),
      });

      if (willReload) {
        // Auto-reload once (this will navigate away)
        console.log('[ChunkLoadError] Auto-reloading once to recover...');
        autoReloadOnce();
        // Don't show UI - we're reloading
      } else {
        // Already reloaded, show fallback UI
        console.log('[ChunkLoadError] Already reloaded once, showing fallback UI');
        setShouldShowUI(true);
      }
    } else {
      // Non-chunk error - show UI immediately
      report(ReportEvent.ERROR_BOUNDARY_TRIGGERED, {
        error_type: 'general_error',
        error_message: error.message,
        error_digest: error.digest,
        error_stack: error.stack,
        pathname: window.location.pathname,
        user_agent: navigator.userAgent,
      });
      setShouldShowUI(true);
    }
  }, [error]);

  // Don't render UI while auto-reload is happening
  if (!shouldShowUI) {
    return null;
  }

  const handleAction = () => {
    if (isChunkError) {
      // For chunk errors: hard reload
      window.location.reload();
    } else {
      // For other errors: go home
      window.location.href = '/quest';
    }
  };

  const actionText = isChunkError ? 'Refresh' : 'Go Home';
  const message = isChunkError
    ? 'Failed to load some resources. Please refresh the page.'
    : "Don't worry, let's go back to the home page";

  // 显示友好的错误提示UI
  return (
    <div className="w-full h-screen flex flex-col justify-center items-center gap-8 px-4">
      <div className="text-center">
        <h1
          className="emphasize-text text-3xl mb-4"
          style={{ color: colors.mainTextColor }}
        >
          Something went wrong
        </h1>
        <p
          className="display-text text-lg"
          style={{ color: colors.mainTextColor }}
        >
          {message}
        </p>
      </div>

      <NormalButton
        onClick={handleAction}
        colorType="green"
        className="w-48"
      >
        {actionText}
      </NormalButton>
    </div>
  );
}
